/**
 * CollaborativeSessionPanel
 *
 * Orchestrates the multi-participant collaborative tool run flow.
 *
 * Phases:
 *   prompt     – facilitator chooses "Run with Team" or "Run Solo"
 *   collecting – participants submit; live or hidden tally shown per voteMode
 *   reviewing  – votes revealed, facilitator reviews aggregates (AggregationView)
 *   finalized  – results captured, read-only summary shown
 *
 * Real-time behaviour:
 *   - Tool session is persisted to Supabase; all clients subscribe via
 *     toolSessionStore (postgres_changes)
 *   - Facilitator sees FacilitatorControlPanel for phase/voteMode controls
 *   - Presence avatars show who is currently online in the session
 */

import { useState, useMemo, useEffect } from 'react';
import { AuthorizationGate } from '../AuthorizationGate';
import {
  Users,
  CheckCircle2,
  Clock,
  Gavel,
  Play,
  UserCheck,
  ArrowRight,
  Wifi,
  WifiOff,
  Activity,
  EyeOff,
} from 'lucide-react';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { AggregationView } from './AggregationView';
import { FacilitatorControlPanel } from './FacilitatorControlPanel';
import type { ToolOutputType } from '../../../lib/toolRegistry';
import type { ToolResultItem } from '../ToolWrapper';
import type { FinalDecision } from '../../../types/prioritization/toolSession';
import type { PresentUser } from '../../../stores/prioritization/presenceStore';

export interface CollaborativeSessionPanelProps {
  toolType: string;
  toolName: string;
  outputType: ToolOutputType;
  sessionId: string;
  workspaceId: string;
  participants: { id: string; name: string }[];
  currentUserId: string;
  currentUserName: string;
  isFacilitator: boolean;
  facilitatorId: string;
  facilitatorName: string;
  toolItems: { id: string; title: string; description?: string }[];
  onRunSolo: () => void;
  onGetResults: () => ToolResultItem[];
  onFinalResults: (results: ToolResultItem[]) => void;
  existingToolSessionId?: string;
  /** Online users from presenceStore — optional; graceful if not provided */
  presentUsers?: PresentUser[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981',
  '#3b82f6', '#8b5cf6', '#f97316', '#14b8a6',
];

function avatarInitials(name: string): string {
  return name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// ─── Live tally bar for a single item's dot count ────────────────────────────

function DotTallyBar({
  itemId,
  itemTitle,
  responses,
}: {
  itemId: string;
  itemTitle: string;
  responses: { results: { id: string; score?: number; rank?: number; category?: string }[]; participantName: string }[];
}) {
  const totalVotes = responses.reduce((sum, r) => {
    const item = r.results.find(ri => ri.id === itemId);
    return sum + (item?.score ?? item?.rank ?? 0);
  }, 0);

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-28 truncate text-[var(--text-sec)]" title={itemTitle}>
        {itemTitle}
      </span>
      <div className="flex-1 h-2 rounded-full bg-[var(--hover-tint)] overflow-hidden">
        <div
          className="h-full rounded-full bg-[var(--slate)] dark:bg-[var(--slate)] transition-all duration-500"
          style={{ width: `${Math.min(totalVotes * 10, 100)}%` }}
        />
      </div>
      <span className="tabular-nums text-[var(--text-ter)] w-6 text-right">
        {totalVotes}
      </span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CollaborativeSessionPanel({
  toolType,
  toolName,
  outputType,
  sessionId,
  workspaceId,
  participants,
  currentUserId,
  currentUserName,
  isFacilitator,
  facilitatorId,
  facilitatorName,
  toolItems,
  onRunSolo,
  onGetResults,
  onFinalResults,
  existingToolSessionId,
  presentUsers = [],
}: CollaborativeSessionPanelProps) {
  const createToolSession = useToolSessionStore(s => s.createToolSession);
  const subscribeToToolSession = useToolSessionStore(s => s.subscribeToToolSession);
  const submitParticipantResponse = useToolSessionStore(s => s.submitParticipantResponse);
  const computeAggregates = useToolSessionStore(s => s.computeAggregates);
  const toolSessions = useToolSessionStore(s => s.toolSessions);
  const allResponses = useToolSessionStore(s => s.participantResponses);

  const [toolSessionId, setToolSessionId] = useState<string | null>(existingToolSessionId ?? null);
  const [reviewing, setReviewing] = useState(false);
  const [selfSubmitted, setSelfSubmitted] = useState(false);
  const [phase, setLocalPhase] = useState<'prompt' | 'collecting'>(
    existingToolSessionId ? 'collecting' : 'prompt'
  );

  // Subscribe to an existing tool session when component mounts with one
  useEffect(() => {
    if (existingToolSessionId) {
      void subscribeToToolSession(existingToolSessionId);
    }
  }, [existingToolSessionId, subscribeToToolSession]);

  const session = useMemo(
    () => toolSessions.find(s => s.id === toolSessionId),
    [toolSessions, toolSessionId]
  );

  const responses = useMemo(
    () => allResponses.filter(r => r.toolSessionId === toolSessionId),
    [allResponses, toolSessionId]
  );

  const completedResponses = useMemo(() => responses.filter(r => r.isComplete), [responses]);

  // Depend on the responses array, not a count — an in-place revision (same
  // submitted count, changed result content) must still recompute. See
  // docs/3-system/architecture-notes/
  // live-session-participant-view-and-results-visibility.md §6.1.
  const aggregates = useMemo(
    () => (toolSessionId ? computeAggregates(toolSessionId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toolSessionId, completedResponses]
  );

  const sessionPhase = session?.phase ?? 'collecting';
  const voteMode = session?.voteMode ?? 'hidden';
  const allSubmitted = completedResponses.length >= participants.length && participants.length > 0;

  // Transition to reviewing when facilitator reveals in another browser
  useEffect(() => {
    if (sessionPhase === 'revealed' && isFacilitator) {
      setReviewing(true);
    }
    if (sessionPhase === 'finalized') {
      setReviewing(false);
    }
  }, [sessionPhase, isFacilitator]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleStartCollaborative = async () => {
    const id = await createToolSession({
      sessionId,
      workspaceId,
      toolType,
      toolName,
      outputType,
      mode: 'sync',
      participantIds: participants.map(p => p.id),
      participantNames: Object.fromEntries(participants.map(p => [p.id, p.name])),
      facilitatorId,
      facilitatorName,
      items: toolItems.map(i => ({ id: i.id, title: i.title, description: i.description })),
      voteMode: 'hidden',
    });
    setToolSessionId(id);
    setLocalPhase('collecting');
  };

  const handleSubmitSelf = async () => {
    if (!toolSessionId) return;
    const results = onGetResults();
    if (results.length === 0) return;
    await submitParticipantResponse({
      toolSessionId,
      workspaceId,
      participantId: currentUserId,
      participantName: currentUserName,
      results,
    });
    setSelfSubmitted(true);
  };

  const handleFinalized = (results: ToolResultItem[]) => {
    onFinalResults(results);
    setReviewing(false);
  };

  const handleFinalDecision = (decision: FinalDecision) => {
    onFinalResults(decision.finalResults);
    setReviewing(false);
  };

  // ── Presence helpers ──────────────────────────────────────────────────────

  function isUserOnline(userId: string): boolean {
    return presentUsers.some(u => u.userId === userId);
  }

  // ── State: Prompt ─────────────────────────────────────────────────────────

  if (phase === 'prompt') {
    return (
      <div className="border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 p-5 mb-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/50 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-[var(--text-sec)]">
              {participants.length} participants in this session
            </h3>
            <p className="text-sm text-[var(--text-sec)] mt-0.5">
              Run this tool collaboratively so everyone contributes their own scores, then review the aggregated results together.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleStartCollaborative}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[var(--slate)] dark:bg-[var(--slate)] hover:bg-[var(--slate)] dark:hover:bg-[var(--slate)] shadow-sm"
              >
                <Users className="h-4 w-4" />
                Run with Team
              </button>
              <button
                onClick={onRunSolo}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] bg-[var(--bg-raised)] dark:bg-[var(--slate)]/30 border border-[var(--slate)] dark:border-[var(--slate)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/60"
              >
                <Play className="h-4 w-4" />
                Run Solo
              </button>
            </div>
            {/* Participant preview */}
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-[var(--text-sec)]">Participants:</span>
              <div className="flex -space-x-1.5">
                {participants.slice(0, 6).map((p, i) => (
                  <div
                    key={p.id}
                    className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center border-2 border-[var(--slate)] dark:border-[var(--slate)]"
                    style={{ backgroundColor: COLORS[i % COLORS.length], zIndex: participants.length - i }}
                    title={p.name}
                  >
                    {avatarInitials(p.name)}
                  </div>
                ))}
                {participants.length > 6 && (
                  <div className="w-6 h-6 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)] text-[var(--text-sec)] text-xs font-bold flex items-center justify-center border-2 border-[var(--slate)] dark:border-[var(--slate)]">
                    +{participants.length - 6}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── State: AggregationView overlay ────────────────────────────────────────

  if (reviewing && toolSessionId) {
    return (
      <div className="fixed inset-0 z-[70] bg-[var(--bg-raised)] flex flex-col">
        <AggregationView
          toolSessionId={toolSessionId}
          onFinalize={handleFinalDecision}
          onClose={() => setReviewing(false)}
        />
      </div>
    );
  }

  // ── State: Finalized ──────────────────────────────────────────────────────

  if (sessionPhase === 'finalized') {
    return (
      <div className="border border-[var(--green)] dark:border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/30 p-4 mb-4">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
          <CheckCircle2 className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
          Results captured — {completedResponses.length} of {participants.length} participants contributed
        </div>
      </div>
    );
  }

  // ── State: Collecting / Revealed ──────────────────────────────────────────

  const showLiveTally = voteMode === 'live' || sessionPhase === 'revealed';

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 mb-4 shadow-sm space-y-3">

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-pri)]">
            Collaborative
          </span>
          {/* Realtime / reveal status badge */}
          {sessionPhase === 'revealed' ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 text-[var(--mustard)] dark:text-[var(--mustard)]">
              <Activity className="w-2.5 h-2.5" />
              Votes revealed
            </span>
          ) : voteMode === 'live' ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)]">
              <Wifi className="w-2.5 h-2.5" />
              Live tally
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-ter)]">
              <EyeOff className="w-2.5 h-2.5" />
              Hidden
            </span>
          )}
        </div>

        {/* Facilitator: open AggregationView */}
        {isFacilitator && completedResponses.length > 0 && sessionPhase !== 'revealed' && (
          <button
            onClick={() => setReviewing(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[var(--slate)] dark:bg-[var(--slate)] hover:bg-[var(--slate)] dark:hover:bg-[var(--slate)]"
          >
            Review Results
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Participant status row */}
      <div className="flex flex-wrap gap-3">
        {participants.map((p, i) => {
          const submitted = completedResponses.some(r => r.participantId === p.id);
          const online = isUserOnline(p.id);
          const isSelf = p.id === currentUserId;
          const color = COLORS[i % COLORS.length];

          return (
            <div key={p.id} className="flex flex-col items-center gap-1">
              <div className="relative">
                {/* Avatar */}
                <div
                  className={`w-9 h-9 rounded-full text-white text-xs font-bold flex items-center justify-center transition-opacity ${!submitted ? 'opacity-40' : ''}`}
                  style={{ backgroundColor: color }}
                  title={`${p.name}${online ? ' (online)' : ''}`}
                >
                  {avatarInitials(p.name)}
                </div>
                {/* Submission status badge */}
                {submitted ? (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[var(--green)] rounded-full flex items-center justify-center border-2 border-white">
                    <CheckCircle2 className="h-2.5 w-2.5 text-white" />
                  </div>
                ) : (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[var(--rule-bright)] rounded-full flex items-center justify-center border-2 border-white">
                    <Clock className="h-2.5 w-2.5 text-white" />
                  </div>
                )}
                {/* Online presence dot */}
                {online && (
                  <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[var(--green)] rounded-full border-2 border-white" />
                )}
              </div>
              <span className="text-xs text-[var(--text-ter)] max-w-[60px] truncate text-center">
                {isSelf ? 'You' : p.name.split(' ')[0]}
              </span>
            </div>
          );
        })}
      </div>

      {/* Live tally (visible when voteMode=live or phase=revealed) */}
      {showLiveTally && completedResponses.length > 0 && toolItems.length > 0 && (
        <div className="bg-[var(--surface)] p-3 space-y-1.5">
          <div className="text-xs font-medium text-[var(--text-ter)] mb-2 flex items-center gap-1">
            <Activity className="w-3 h-3" />
            {sessionPhase === 'revealed' ? 'Final tally' : 'Running tally'}
          </div>
          {toolItems.slice(0, 8).map(item => (
            <DotTallyBar
              key={item.id}
              itemId={item.id}
              itemTitle={item.title}
              responses={completedResponses}
            />
          ))}
          {toolItems.length > 8 && (
            <p className="text-xs text-[var(--text-ter)]">
              +{toolItems.length - 8} more items…
            </p>
          )}
        </div>
      )}

      {/* Self-submission CTA */}
      {!selfSubmitted ? (
        <div className="flex items-center gap-3 p-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 border border-[var(--slate)] dark:border-[var(--slate)]">
          <UserCheck className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0" />
          <span className="text-sm text-[var(--text-sec)] flex-1">
            Complete the tool above, then submit your results to the session.
          </span>
          <AuthorizationGate
            action="session.participate"
            resource={{ type: 'session' as const, id: toolSessionId ?? '' }}
          >
            <button
              onClick={handleSubmitSelf}
              disabled={sessionPhase === 'finalized'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[var(--slate)] dark:bg-[var(--slate)] hover:bg-[var(--slate)] dark:hover:bg-[var(--slate)] whitespace-nowrap flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Gavel className="h-3.5 w-3.5" />
              Submit My Results
            </button>
          </AuthorizationGate>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 bg-[var(--green-dim)] dark:bg-[var(--green)]/30 border border-[var(--green)] dark:border-[var(--green)]">
          <CheckCircle2 className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
          <span className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            Your results are submitted.
            {!isFacilitator && completedResponses.length < participants.length && (
              <span className="text-[var(--green)] dark:text-[var(--text-ter)]">
                {' '}Waiting for {participants.length - completedResponses.length} more…
              </span>
            )}
          </span>
        </div>
      )}

      {/* Connection status for participants */}
      {!isFacilitator && presentUsers.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)]">
          {presentUsers.length > 0 ? (
            <>
              <Wifi className="w-3 h-3 text-[var(--green)]" />
              {presentUsers.length} online
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3" />
              Connecting…
            </>
          )}
        </div>
      )}

      {/* Facilitator controls */}
      {isFacilitator && toolSessionId && (
        <FacilitatorControlPanel
          toolSessionId={toolSessionId}
          phase={sessionPhase}
          voteMode={voteMode}
          allSubmitted={allSubmitted}
          submittedCount={completedResponses.length}
          participantCount={participants.length}
          aggregates={aggregates}
          facilitatorId={facilitatorId}
          facilitatorName={facilitatorName}
          onFinalized={handleFinalized}
        />
      )}
    </div>
  );
}
